<template>
  <div class="header-curated-stays-dropdown w-full shadow-lg">
    <div class="header-curated-stays-dropdown__container">
      <!-- Left: Image -->
      <div class="header-curated-stays-dropdown__image-section">
        <img alt="Curated Stays" class="header-curated-stays-dropdown__image" loading="lazy" :src="curatedStayImage" />
      </div>
      <!-- Right: Content -->
      <div class="header-curated-stays-dropdown__content">
        <h3 class="header-curated-stays-dropdown__section-title">Experiential Stays</h3>
        <div class="header-curated-stays-dropdown__list">
          <NuxtLink v-for="group in tagGroupItems" :key="group.name" class="header-curated-stays-dropdown__item" :to="group.link" @click="onClickStaysGroup(group.name)">
            <span>{{ group.name }}</span>
            <span class="header-curated-stays-dropdown__arrow" />
          </NuxtLink>
        </div>
        <NuxtLink class="header-curated-stays-dropdown__view-more" :to="CURATED_STAYS_VIEW_MORE_URL" @click="onClickViewMore">
          <span>View more</span>
          <span class="header-curated-stays-dropdown__view-more-arrow" />
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { WabunkaTagDictByGroupName } from '@/models/wabunka/wabunkaTag';
import curatedStayImage from '@/wabunka-src/common/assets/img/nav/curatedStay.png';
import { sendGAWabunkaClickHeaderNavEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/clickHeaderNav';
import { CURATED_STAYS_VIEW_MORE_URL, CURATED_STAYS_THEME_SLUG, CURATED_STAYS_EXCLUDED_GROUPS } from '@/wabunka-src/constants/navigation';

interface Props {
  destinationTagGroups: WabunkaTagDictByGroupName;
}

const props = defineProps<Props>();

const tagGroupItems = computed(() => {
  const groups = Object.entries(props.destinationTagGroups)
    .filter(([name]) => !CURATED_STAYS_EXCLUDED_GROUPS.includes(name))
    .map(([name, tags]) => {
      const destinations = tags.map((tag) => tag.webPath.replace(/^\//, '')).join(',');
      return {
        name,
        link: `/s/experiences/?destinations=${destinations}&themes=${CURATED_STAYS_THEME_SLUG}`,
      };
    });
  return groups;
});

const onClickStaysGroup = (name: string) => {
  sendGAWabunkaClickHeaderNavEvent('stays', name, 'header');
};

const onClickViewMore = () => {
  sendGAWabunkaClickHeaderNavEvent('stays', 'view_more', 'header');
};
</script>

<style lang="scss" scoped>
.header-curated-stays-dropdown {
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(20px);
  &__container {
    display: flex;
    gap: 32px;
    max-width: 1400px;
  }
  &__image-section {
    flex-shrink: 0;
    width: 18%;
    max-width: 250px;
    aspect-ratio: 269 / 336;
    transition: transform 0.2s ease;
  }
  &__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  &__content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 32px 48px 32px 0;
  }
  &__section-title {
    font-family: 'Spectral', serif;
    font-size: 20px;
    font-weight: 500;
    line-height: 28px;
    letter-spacing: 0.5px;
    text-transform: capitalize;
    color: #b8860b;
    margin-bottom: 4px;
  }
  &__list {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px 32px;
  }
  &__item {
    display: flex;
    align-items: center;
    gap: 4px;
    font-family: 'Spectral', serif;
    font-size: 17px;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0;
    color: #171717;
    text-decoration: none;
    transition: color 0.3s ease;
    &:hover {
      color: #b8860b;
      .header-curated-stays-dropdown__arrow {
        border-color: #b8860b;
      }
    }
  }
  &__arrow {
    width: 6px;
    height: 6px;
    border-top: solid 1px #333;
    border-right: solid 1px #333;
    transform: rotate(45deg);
    transition: border-color 0.3s ease;
  }
  &__view-more {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: auto;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    font-family: 'Spectral', serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0;
    vertical-align: middle;
    color: #171717;
    transition: color 0.3s ease;
    &:hover {
      color: #b8860b;
      .header-curated-stays-dropdown__view-more-arrow {
        border-color: #b8860b;
      }
    }
  }
  &__view-more-arrow {
    width: 6px;
    height: 6px;
    border-top: solid 1px #333;
    border-right: solid 1px #333;
    transform: rotate(45deg);
    transition: border-color 0.3s ease;
  }
}

:global(.dropdown-enter-active) .header-curated-stays-dropdown__image-section {
  opacity: 0;
  animation: curatedStaysImageFadeIn 0.1s ease 0.15s forwards;
}

:global(.dropdown-leave-active) .header-curated-stays-dropdown__image-section {
  opacity: 0;
  transition: opacity 0.05s ease;
}

@keyframes curatedStaysImageFadeIn {
  to {
    opacity: 1;
  }
}
</style>
